import type { SupportedLanguage } from '@/i18n';

export type SpeechRecognitionConstructor = new () => {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onstart: ((ev: Event) => void) | null;
  onend: ((ev: Event) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onresult: ((ev: unknown) => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

export type SttUiState = 'idle' | 'listening' | 'error';

/** `oneshot` ends after a browser-detected pause; `manual` listens until the user stops. */
export type SpeechRecognitionMode = 'oneshot' | 'manual';

export type SttErrorType = 'permission_denied' | 'no_speech';

export interface SpeechRecognitionErrorEvent {
  error?: string;
  message?: string;
}

const speechRecognitionLang: Record<SupportedLanguage, string> = {
  en: 'en-US',
  de: 'de-DE',
  bg: 'bg-BG',
};

export function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  const w = window as Window & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function getSpeechRecognitionLang(lang: string): string {
  const prefix = (lang.split('-')[0] || 'en') as SupportedLanguage;
  return speechRecognitionLang[prefix] ?? speechRecognitionLang.en;
}

export function mapSpeechError(errorCode: string | undefined): SttErrorType | null {
  if (errorCode === 'not-allowed' || errorCode === 'service-not-allowed') {
    return 'permission_denied';
  }
  if (errorCode === 'no-speech') {
    return 'no_speech';
  }
  return null;
}

export function extractTranscript(ev: unknown): string {
  const results = (ev as { results?: ArrayLike<{ 0?: { transcript?: string } }> })?.results;
  if (!results) return '';

  let transcript = '';
  for (let i = 0; i < results.length; i++) {
    const alt = results[i]?.[0];
    if (alt?.transcript) transcript += alt.transcript;
  }
  return String(transcript).trim();
}
